
import Vue from 'vue';
import _ from 'lodash';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { Route } from 'vue-router';
import { Action, Getter, Mutation, State } from 'vuex-class';
import ContentPreview from '@/components/Editor/ContentPreview.vue';
import store from '@/store';
import { Page } from '@/types/page';
import { Layout } from '@/types/categories';
import { codemirror } from 'vue-codemirror-lite';
import Editor from '@/components/Editor/Editor.vue';
import BannerEditor from '@/components/Editor/BannerEditor.vue';
import PageMetaDataInput from '@/components/Page/PageMetaDataInput.vue';
import TwitterPreview from '@/components/Editor/TwitterPreview.vue';
import LinkedInPreview from '@/components/Editor/LinkedInPreview.vue';
import FacebookPreview from '@/components/Editor/FacebookPreview.vue';
require('codemirror/mode/htmlmixed/htmlmixed');
require('codemirror/addon/edit/closetag');
require('codemirror/addon/edit/matchtags');

@Component({
  components: {
    BannerEditor,
    ContentPreview,
    codemirror,
    Editor,
    FacebookPreview,
    LinkedInPreview,
    PageMetaDataInput,
    TwitterPreview,
  },
})
export default class PageEditor extends Vue {
  @Prop() id!: string;
  @Action('pageEditor/savePage') savePage!: Function;
  @Action('notifications/addNotification') addNotification!: Function;
  @Action('categories/getLayouts') getLayouts!: Function;
  @Getter('categories/layouts') layouts!: Layout[];
  @Mutation('pageEditor/setPageTitle') setPageTitle!: Function;
  @Mutation('pageEditor/setPageContent') setPageContent!: Function;
  @Mutation('pageEditor/setPageSlug') setPageSlug!: Function;
  @Mutation('pageEditor/setPageLayoutId') setPageLayoutId!: Function;
  @Mutation('pageEditor/setPageSubTitle') setPageSubTitle!: Function;
  @Mutation('pageEditor/setPageCTAText') setPageCTAText!: Function;
  @Mutation('pageEditor/setPageStrapline') setPageStrapline!: Function;
  @Mutation('pageEditor/setPageDescription') setPageDescription!: Function;
  @State('pageEditor') page!: Page;

  slug: string | null | undefined = null;
  dialog: boolean = false;
  isSaving: boolean = false;
  isDirty: boolean = false;
  tabs: number = 0;
  toggleEditMode: number = 0;
  codemirrorOptions: object = {
    mode: 'htmlmixed',
    tabSize: 4,
    lineNumbers: true,
    lineWrapping: true,
    matchTags: true,
    closeTag: true,
    viewportMargin: Infinity,
  };

  validation: object = {
    title: [(v: any) => !!v || 'A title is required'],
    slug: [(v: any) => !!v || 'A slug is required'],
    description: [(v: any) => !!v || 'A description is required'],
  };

  isEditorValid: boolean = true;

  userHasAlteredSlug: boolean = false;

  get titleProgress(): number {
    return Math.min(100, Math.floor(this.pageTitle.length * 0.6));
  }

  get titleColour(): string {
    if (this.pageTitle.length > 100) {
      return 'error';
    }
    const index = Math.min(2, Math.floor(this.pageTitle.length / 30));
    return ['error', 'warning', 'success'][index];
  }

  get editorContent(): string {
    return this.page.content as string;
  }

  get pageTitle(): string {
    return this.page.meta ? this.page.meta.title : '';
  }
  set pageTitle(value) {
    if (this.page.meta.slug === '' || this.userHasAlteredSlug === false) {
      this.page.meta.slug = _.kebabCase(value);
    }
    this.setPageTitle(value);
  }

  get pageContent(): string | undefined | null {
    return this.page.content;
  }
  set pageContent(value) {
    this.setPageContent(value);
  }

  get pageLayoutId(): string | null {
    return this.page.layout_id;
  }
  set pageLayoutId(value) {
    this.setPageLayoutId(value);
  }

  get pageSlug(): string | null | undefined {
    return this.page.meta.slug;
  }
  set pageSlug(slug: string | null | undefined) {
    this.page.meta.slug = slug ? _.kebabCase(slug) : '';
  }

  get pageSubTitle(): string | null {
    return this.page.sub_title;
  }
  set pageSubTitle(value) {
    this.setPageSubTitle(value);
  }

  get pageCTAText(): string | null {
    return this.page.cta_text;
  }
  set pageCTAText(value) {
    this.setPageCTAText(value);
  }

  get pageStrapline(): string | null {
    return this.page.strapline;
  }
  set pageStrapline(value) {
    this.setPageStrapline(value);
  }

  get pageDescription(): string | null {
    return this.page.description;
  }
  set pageDescription(value) {
    this.setPageDescription(value);
  }

  get orderedLayouts(): Layout[] {
    const pageLayouts = this.layouts.filter(layout => layout.type === 'page');
    return _.orderBy(pageLayouts, ['name'], ['asc']);
  }

  onUserSlugChange(slug: string | null) {
    if (_.isEmpty(slug)) {
      this.userHasAlteredSlug = false;
      this.pageSlug = this.pageTitle;
    } else {
      this.userHasAlteredSlug = true;
    }
  }

  async updatePage() {
    if (this.formIsValid()) {
      this.isSaving = true;
      await this.savePage();
      this.isSaving = false;
    }
  }

  onEditorBlur() {
    return (this.isEditorValid =
      this.editorContent !== '<p>&nbsp;</p>' && !!this.editorContent);
  }

  formIsValid(): boolean {
    return [
      (<HTMLFormElement>this.$refs.title).validate(true),
      this.onEditorBlur(),
      (<HTMLFormElement>this.$refs.slug).validate(true),
      (<HTMLFormElement>this.$refs.description).validate(true),
    ].every(v => v);
  }

  async beforeRouteEnter(
    to: Route,
    from: Route,
    next: Function,
  ): Promise<void> {
    if (to.params.hasOwnProperty('id')) {
      await store.dispatch('pageEditor/getPage', to.params.id);
    } else {
      await store.commit('pageEditor/resetPageEditor');
    }
    next();
    await store.dispatch('categories/getLayouts');
    next();
  }

  async beforeRouteUpdate(
    to: Route,
    from: Route,
    next: Function,
  ): Promise<void> {
    if (to.params.hasOwnProperty('id')) {
      await store.dispatch('pageEditor/getPage', to.params.id);
    } else {
      await store.commit('pageEditor/getPage');
    }
    next();
  }

  @Watch('page.id')
  onPageIdChanged(id?: string, oldId?: string) {
    if (id && oldId === undefined) {
      this.$router.replace({ name: 'edit-page', params: { id } });
    }
  }
}
